import React, { useLayoutEffect, useState, useCallback } from 'react';
import type { IProfileData } from '@intigral/types';

interface HeaderMenuItem {
    id: string;
    title: string;
}

interface Profile {
    id: string;
    name: string;
}

/**
 * Custom hook to handle responsive menu resizing behavior
 * @param headerMenuItems - Array of menu items to be displayed
 * @param selectedProfile - Currently selected user profile
 * @returns Object containing the menu reference callback
 */
const useResizeMenu = (headerMenuItems: HeaderMenuItem[], selectedProfile: IProfileData | null) => {
    let resizeTimer: ReturnType<typeof setTimeout>;
    const [node, setNode] = useState<HTMLElement | null>(null);

    const initMenuLength = () => {
        if (!node) return;
        const menuList = node.querySelectorAll<HTMLElement>('.mainNav-list > li:not(.active-profile)');
        const dropList = document.querySelector<HTMLElement>('.drop-expand');
        if (!menuList?.length || !dropList) return;

        const dropListItem = dropList.querySelectorAll<HTMLElement>('li');

        if (window.innerWidth < 770) return false;

        let visibleDropFlag = false;
        [...menuList].reverse().forEach((item, i) => {
            const dropItem = [...dropListItem].reverse()[i];
            if (!dropItem?.classList) return;

            dropItem.classList.remove('visible');
            const itemFirst = menuList[0].offsetTop;

            if (item.offsetTop > itemFirst) {
                visibleDropFlag = true;
                item.classList.add('menuHidden');
                dropItem.classList.add('visible');
            } else {
                item.classList.remove('menuHidden');
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            visibleDropFlag ? dropList.classList.add('visible') : dropList.classList.remove('visible');
        });
    };

    const toggleExpandClass = (): void => {
        const ico = document.querySelector<HTMLElement>('.drop-expand .ico');
        (ico?.parentNode as HTMLElement)?.classList.toggle('expand');
    };

    const removeExpandOnOutsideClick = (event: MouseEvent): void => {
        const ico = document.querySelector<HTMLElement>('.drop-expand .ico');
        if (!ico) return;

        const target = event.target as HTMLElement;
        if (!target.closest('.drop-expand')) {
            event.stopPropagation();
            (ico.parentNode as HTMLElement)?.classList.remove('expand');
        }
    };

    const onResize = (): void => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(initMenuLength, 300);
    };

    const removeEventListeners = (): void => {
        const ico = document.querySelector<HTMLElement>('.drop-expand .ico');
        if (!ico) return;

        ico.removeEventListener('click', toggleExpandClass);
        document.removeEventListener('click', removeExpandOnOutsideClick);
        clearTimeout(resizeTimer);
    };

    const initExpandMenu = (): boolean | void => {
        const ico = document.querySelector<HTMLElement>('.drop-expand .ico');
        if (window.innerWidth < 770 || !ico) return false;

        ico.addEventListener('click', toggleExpandClass);
        document.addEventListener('click', removeExpandOnOutsideClick);
    };

    const resizeMenu = (node: HTMLElement): void => {
        window.addEventListener('resize', onResize);
        initMenuLength();
        initExpandMenu();
    };

    useLayoutEffect(() => {
        if (!headerMenuItems.length || !node || !selectedProfile) return;

        removeEventListeners();
        resizeMenu(node);

        return () => {
            removeEventListeners();
            clearTimeout(resizeTimer);
        };
    }, [node, headerMenuItems, selectedProfile]);

    const menuRef = useCallback(
        (node: HTMLElement | null) => {
            if (node !== null) {
                setNode(node);
            }
        },
        [headerMenuItems]
    );

    return { menuRef };
};

export default useResizeMenu;
